import {
  AvatarService,
  getServerAvatarIdForSelect,
  type PlayerAvatar,
} from '@core/Web/services/AvatarService';
import { readPersistedGameNickname } from '@core/gameengine/state/auth';
import { SKYX_NEW_PLAYER_AVATAR_CHANGED } from '@core/lobby/skyxNewPanelEvents';
import { GlobalBus } from '@core/Bus/EventBus';

const LOCAL_AVATAR_KEY_RE = /^avatar([1-9]|1[0-5])$/i;

export type SkyxAvatarKey =
  `avatar${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15}`;

function isKnownAvatarKey(value: string | undefined): value is SkyxAvatarKey {
  return Boolean(value && LOCAL_AVATAR_KEY_RE.test(value));
}

/** Cached catalogue from GET /api/player/v1/avatars (CDN URLs only). */
let serverAvatarCatalog: PlayerAvatar[] = [];
let serverAvatarPool: string[] = [];
let cachedSelectedAvatarUrl: string | null = null;
let serverAvatarFetchPromise: Promise<string[]> | null = null;

function hashPlayerId(playerId: string): number {
  const hash = playerId.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return Math.abs(hash);
}

function isAbsoluteAvatarUrl(value: string): boolean {
  return /^https?:\/\//i.test(value);
}

function resolveFromCatalog(
  catalog: PlayerAvatar[],
  stored: StoredPlayerAvatar
): string {
  if (stored.imageUrl && isAbsoluteAvatarUrl(stored.imageUrl)) {
    return stored.imageUrl;
  }

  if (stored.avatarId) {
    const byId = catalog.find(entry => {
      const serverId = getServerAvatarIdForSelect(entry);
      return (
        serverId === stored.avatarId ||
        entry.id === stored.avatarId ||
        entry.serverAvatarId === stored.avatarId
      );
    });
    if (byId?.imageUrl) return byId.imageUrl;
  }

  if (stored.avatarKey) {
    const byKey = catalog.find(entry => entry.avatarKey === stored.avatarKey);
    if (byKey?.imageUrl) return byKey.imageUrl;
  }

  return '';
}

function resolveLocalPlayerAvatarUrl(catalog: PlayerAvatar[]): string {
  if (cachedSelectedAvatarUrl) {
    return cachedSelectedAvatarUrl;
  }

  const selected = catalog.find(entry => entry.selected);
  if (selected?.imageUrl) {
    return selected.imageUrl;
  }

  const stored = readStoredPlayerAvatar();
  if (stored) {
    if (stored.imageUrl && isAbsoluteAvatarUrl(stored.imageUrl)) {
      return stored.imageUrl;
    }
    const fromStored = resolveFromCatalog(catalog, stored);
    if (fromStored) return fromStored;
  }

  return '';
}

function syncStoredSelectionFromCatalog(catalog: PlayerAvatar[]): void {
  const selected = catalog.find(entry => entry.selected);
  if (!selected?.imageUrl) return;

  cachedSelectedAvatarUrl = selected.imageUrl;
  const next = storedSelectionFromPlayerAvatar(selected);
  const current = readStoredPlayerAvatar();
  if (
    current?.imageUrl === next.imageUrl &&
    current?.avatarId === next.avatarId &&
    current?.avatarKey === next.avatarKey
  ) {
    return;
  }

  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

/** Sync lookup: deterministic CDN avatar for a player id from the server pool. */
export function getServerAvatarUrlForPlayer(playerId: string): string | null {
  if (!serverAvatarPool.length || !playerId) return null;
  return serverAvatarPool[hashPlayerId(playerId) % serverAvatarPool.length];
}

function hydrateAvatarCacheFromService(): boolean {
  const peeked = AvatarService.getInstance().peekPlayerAvatarsCache();
  if (!peeked?.length) return false;

  serverAvatarCatalog = peeked.filter(entry => Boolean(entry.imageUrl));
  const urls = serverAvatarCatalog
    .map(entry => entry.imageUrl)
    .filter((url): url is string => Boolean(url));

  if (!urls.length) return false;

  serverAvatarPool = urls;
  syncStoredSelectionFromCatalog(serverAvatarCatalog);
  return true;
}

/** Prefetch player avatars from the server. Safe to call multiple times. */
export function ensureServerAvatarPool(
  options: { force?: boolean } = {}
): Promise<string[]> {
  if (!options.force) {
    if (!serverAvatarPool.length) {
      hydrateAvatarCacheFromService();
    }
    if (serverAvatarPool.length) {
      return Promise.resolve(serverAvatarPool);
    }
  }
  if (serverAvatarFetchPromise) return serverAvatarFetchPromise;

  serverAvatarFetchPromise = AvatarService.getInstance()
    .fetchPlayerAvatars(options.force ? { force: true } : undefined)
    .then(list => {
      serverAvatarCatalog = list.filter(entry => Boolean(entry.imageUrl));
      const urls = serverAvatarCatalog
        .map(entry => entry.imageUrl)
        .filter((url): url is string => Boolean(url));

      if (urls.length) {
        serverAvatarPool = urls;
        syncStoredSelectionFromCatalog(serverAvatarCatalog);
        GlobalBus.emit(SKYX_NEW_PLAYER_AVATAR_CHANGED);
        GlobalBus.emit('player:avatar-changed');
      }

      return serverAvatarPool;
    })
    .catch(err => {
      console.warn('[skyx_new] Failed to fetch server avatar pool', err);
      return serverAvatarPool;
    })
    .finally(() => {
      serverAvatarFetchPromise = null;
    });

  return serverAvatarFetchPromise;
}

const STORAGE_KEY = 'skyx_new-player-avatar';

export interface StoredPlayerAvatar {
  avatarKey?: SkyxAvatarKey;
  avatarId?: string;
  /** Full CDN URL from GET /api/player/v1/avatars. */
  imageUrl?: string;
}

export function readStoredPlayerAvatar(): StoredPlayerAvatar | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPlayerAvatar;
    if (parsed?.imageUrl && isAbsoluteAvatarUrl(parsed.imageUrl)) {
      return parsed;
    }
    if (parsed?.avatarId || parsed?.avatarKey) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeStoredPlayerAvatar(selection: StoredPlayerAvatar): void {
  if (typeof window === 'undefined') return;
  if (selection.imageUrl && isAbsoluteAvatarUrl(selection.imageUrl)) {
    cachedSelectedAvatarUrl = selection.imageUrl;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  GlobalBus.emit(SKYX_NEW_PLAYER_AVATAR_CHANGED);
  GlobalBus.emit('player:avatar-changed');
}

export function storedSelectionFromPlayerAvatar(
  avatar: PlayerAvatar
): StoredPlayerAvatar {
  const avatarKey = isKnownAvatarKey(avatar.avatarKey)
    ? avatar.avatarKey
    : undefined;

  const serverId = getServerAvatarIdForSelect(avatar);

  return {
    avatarId: serverId ?? undefined,
    imageUrl: avatar.imageUrl,
    avatarKey,
  };
}

export function buildFallbackSystemAvatars(): PlayerAvatar[] {
  return Array.from({ length: 15 }, (_, index) => {
    const key = `avatar${index + 1}` as SkyxAvatarKey;
    return {
      id: key,
      avatarKey: key,
      imageUrl: '',
      label: key,
    };
  });
}

export function isLocalSkyxPlayerKey(
  playerKey: string,
  localPlayerId?: string | null,
  localPlayerNickname?: string | null
): boolean {
  if (!playerKey) return false;
  if (localPlayerId && playerKey === localPlayerId) return true;
  const nickname = localPlayerNickname?.trim() || readPersistedGameNickname();
  return Boolean(nickname && playerKey === nickname);
}

/**
 * Resolve a player's avatar to a server CDN URL (never local manifest assets).
 * Same player id always maps to the same CDN avatar across scoreboard, chat, etc.
 */
export function resolvePlayerAvatarUrl(
  playerId: string,
  localPlayerId?: string | null,
  preferredUrl?: string | null,
  localPlayerNickname?: string | null
): string {
  if (preferredUrl && isAbsoluteAvatarUrl(preferredUrl)) {
    return preferredUrl;
  }

  if (!playerId) return '';

  if (isLocalSkyxPlayerKey(playerId, localPlayerId, localPlayerNickname)) {
    const localUrl = resolveLocalPlayerAvatarUrl(serverAvatarCatalog);
    if (localUrl) return localUrl;
    void ensureServerAvatarPool();
    return '';
  }

  const pooledUrl = getServerAvatarUrlForPlayer(playerId);
  if (pooledUrl) return pooledUrl;

  void ensureServerAvatarPool();
  return '';
}

/** Used by CrashGameController — returns a CDN URL or empty string while loading. */
export function resolvePlayerAvatarPath(
  playerId: string,
  localPlayerId?: string | null,
  localPlayerNickname?: string | null
): string {
  return resolvePlayerAvatarUrl(
    playerId,
    localPlayerId,
    undefined,
    localPlayerNickname
  );
}

export function isPlayerAvatarSelected(
  avatar: PlayerAvatar,
  selected: StoredPlayerAvatar | null
): boolean {
  if (!selected) return false;

  const serverId = getServerAvatarIdForSelect(avatar);
  if (selected.avatarId && serverId && selected.avatarId === serverId) {
    return true;
  }
  if (selected.avatarId && avatar.serverAvatarId === selected.avatarId) {
    return true;
  }
  if (avatar.selected) return true;
  if (selected.imageUrl && avatar.imageUrl === selected.imageUrl) return true;
  if (selected.avatarKey && avatar.avatarKey === selected.avatarKey) {
    return true;
  }
  return false;
}

/** @deprecated Use isPlayerAvatarSelected */
export function isAvatarKeySelected(
  avatarKey: string,
  selected: StoredPlayerAvatar | null
): boolean {
  return Boolean(selected && selected.avatarKey === avatarKey);
}
